import { useEffect, useRef } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@volca/ui/components/ui/empty";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { shouldShowMessageTime } from "@/core/utils/message";
import { MessageDisplay } from "./components/message-display";
import { TEXT_MESSAGE_DIRECTIONS } from "./constants";
import { useGetCustomerTextMessages } from "./query";
import { IMostRecentTextMessage } from "./text-messages";

interface TextMessagesSidebarProps {
  textMessage?: IMostRecentTextMessage | null;
  isOpen: boolean;
}

const TextMessagesSidebar = ({
  textMessage,
  isOpen,
}: TextMessagesSidebarProps) => {
  const {
    data: messages,
    isLoading,
    isError,
    refetch,
  } = useGetCustomerTextMessages(textMessage?.customer.id);

  const viewportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (viewportRef.current) {
      viewportRef.current.scrollTop = viewportRef.current.scrollHeight;
    }
  }, [messages, isOpen]);

  if (isLoading) {
    return (
      <Stack gap={6} className="p-6" align="end">
        <Skeleton className="h-16 w-full max-w-[330px]" />
        <Skeleton className="h-40 w-full max-w-[330px]" />
        <Skeleton className="h-12 w-full max-w-[330px]" />

        <Skeleton className="h-16 w-full max-w-[330px] self-start" />
      </Stack>
    );
  }

  return (
    <div
      ref={viewportRef}
      className="h-full w-full overflow-y-auto pr-2"
      aria-label="Text message conversation"
      role="log"
      aria-live="polite"
    >
      <Stack align="start" gap={2} className="min-h-full pt-6 pr-4 pb-14 pl-6">
        {isError && !messages?.length ? (
          <Empty
            className={emptyStateVariants({ layout: "collection" })}
            role="alert"
          >
            <EmptyHeader>
              <EmptyTitle>Couldn't load this conversation</EmptyTitle>
              <EmptyDescription>Please try again.</EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void refetch()}
              >
                Try again
              </Button>
            </EmptyContent>
          </Empty>
        ) : (
          !messages?.length && (
            <Empty className={emptyStateVariants({ layout: "collection" })}>
              <EmptyHeader>
                <EmptyDescription>
                  No messages in this conversation yet.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          )
        )}
        {messages?.map((message, idx) => (
          <MessageDisplay
            key={message.id}
            message={message.body}
            mediaUrls={message.media_urls}
            isOutbound={message.direction === TEXT_MESSAGE_DIRECTIONS.outbound}
            createdAt={message.created_at}
            // TODO consider enriching messages with this field on load rather than calculating each render
            showTime={shouldShowMessageTime(message, messages[idx + 1])}
          />
        ))}
      </Stack>
    </div>
  );
};

export default TextMessagesSidebar;
